import axiosInstance from '@/lib/network/axios';
import axiosServerInstance from '@/lib/network/server-axios';
import { BackendPaths } from '@/lib/utils';
import { AxiosRequestConfig } from 'axios';
import { JWT } from 'next-auth/jwt';
import {
  CompanyDocumentPayload,
  CompanyDocUploadResponse,
  CreateWithEinPayload,
  endpointMap,
  ExternalKycDocumentPayload,
  KycDocumentPayload,
  KYCVerifyPayload,
  RegisterationStatusResponse,
  UpdateEntityRequest,
  VerifyAccountPayload,
} from '../interfaces/payload';
import { Pricing } from '../interfaces/pricing';
import { Questionnaire } from '../interfaces/questionnaire';
import { TermsConditions } from '../interfaces/termsConditions';
import { VerifyAbnResponse } from '../interfaces/verify-abn';
import { ILinkedBankAccounts, VerifyBSBResponse } from '../interfaces/verify-bank';

export interface RegisterData {
  name: string;
  email: string;
  password: string;
}

export interface RegisterResponse {
  id: string;
  name: string;
  email: string;
  token: string;
}

export const registerUser = async (data: RegisterData): Promise<RegisterResponse> => {
  const res = await axiosInstance.post(BackendPaths.REGISTER, data);
  return res.data;
};

export const logOut = async (): Promise<unknown> => {
  const res = await axiosInstance.post(BackendPaths.LOGOUT);
  return res.data;
};

export const refreshToken = async (token: JWT): Promise<JWT> => {
  const config: AxiosRequestConfig = {};
  if (token.accessToken) {
    config.headers = {
      Authorization: `Bearer ${token.accessToken}`,
    };
  }
  const res = await axiosInstance.post(
    BackendPaths.REFRESH,
    {
      refreshToken: token.refreshToken,
    },
    config,
  );
  return {
    accessToken: res.data.access_token,
    refreshToken: res.data.refresh_token ?? token.refreshToken,
    accessTokenExpires: Date.now() + res.data.expires_in * 1000,
    idToken: res.data.id_token ?? token.idToken,
  };
};

export const getQuestionaire = async (): Promise<Questionnaire[]> => {
  const res = await axiosInstance.get(BackendPaths.QUESTIONNAIRE);
  return res.data;
};

export const getPricing = async (): Promise<Pricing[]> => {
  const res = await axiosInstance.get(BackendPaths.PRICING);
  return res.data;
};

export const getTermsConditions = async (): Promise<TermsConditions[]> => {
  const res = await axiosInstance.get(BackendPaths.TNC);
  return res.data;
};

export const updateEntities = async (updatePayload: UpdateEntityRequest): Promise<unknown> => {
  const endpoint = endpointMap[updatePayload.type];
  if (!endpoint) {
    throw new Error('Invalid update type');
  }

  const requestPayload = { attributes: updatePayload.attributes };
  const res = await axiosInstance.post(endpoint, requestPayload);
  return res.data;
};

export const verifyUserAbn = async (value: string): Promise<VerifyAbnResponse> => {
  const abnPayload = { abn: value };
  const res = await axiosInstance.post(BackendPaths.ONBOARDING, abnPayload);
  return res.data;
};

export const getOrganization = async (): Promise<VerifyAbnResponse> => {
  const res = await axiosInstance.get(BackendPaths.ONBOARDING);
  return res.data;
};

export const sendVerificationEmail = async (value: string): Promise<unknown> => {
  const verificationPayload = { emailId: value };
  const res = await axiosInstance.post(BackendPaths.VERIFYEMAIL, verificationPayload);
  return res.data;
};

export const sendVerificationKYCEmail = async (KYCVerifyPayload: KYCVerifyPayload): Promise<unknown> => {
  try {
    const res = await axiosInstance.post(BackendPaths.VERIFYEMAILKYC, KYCVerifyPayload);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const verifyUserBSB = async (value: string): Promise<VerifyBSBResponse> => {
  try {
    const res = await axiosInstance.get(`${BackendPaths.VERIFYBSB}?bsb=${value}`);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const verifyBankAccount = async (verifyAccountPayload: VerifyAccountPayload): Promise<VerifyBSBResponse> => {
  try {
    const res = await axiosInstance.post(BackendPaths.VERIFYBANKACCOUNT, {
      bankAccount: verifyAccountPayload,
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const checkRegistrationStatus = async (token: string): Promise<RegisterationStatusResponse> => {
  try {
    const res = await axiosServerInstance.get(BackendPaths.REGISTERATIONSTATUS, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return res.data;
  } catch (error: any) {
    throw error;
  }
};

export const kycFileUpload = async (kycDocumentPayload: KycDocumentPayload): Promise<VerifyBSBResponse> => {
  const formData = new FormData();
  formData.append('front', kycDocumentPayload.front, kycDocumentPayload.front.name);
  formData.append('rear', kycDocumentPayload.rear, kycDocumentPayload.rear.name);
  formData.append('selfPortrait', kycDocumentPayload.selfie, kycDocumentPayload.selfie.name);
  try {
    const res = await axiosInstance.post(BackendPaths.KYCSINGLE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const externalKycFileUpload = async (
  externalKycDocUpload: ExternalKycDocumentPayload,
): Promise<VerifyBSBResponse> => {
  const formData = new FormData();
  formData.append('x_kyc_token', externalKycDocUpload.x_kyc_token);
  formData.append('front', externalKycDocUpload.front, externalKycDocUpload.front.name);
  formData.append('rear', externalKycDocUpload.rear, externalKycDocUpload.rear.name);
  formData.append('selfPortrait', externalKycDocUpload.selfie, externalKycDocUpload.selfie.name);
  try {
    const res = await axiosInstance.post(BackendPaths.KYCMULTIPLE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const accountingLogin = async (): Promise<{ auth_url: string }> => {
  try {
    const res = await axiosInstance.get(BackendPaths.ACCOUNTING_LOGIN);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getCustomerTermsConditions = async (): Promise<TermsConditions[]> => {
  const res = await axiosInstance.get(BackendPaths.CUSTOMERTNC);
  return res.data;
};

export const getCustomerCreditLimit = async () => {
  const res = await axiosInstance.get(BackendPaths.CUSTOMERCREDITLIMIT);
  return res.data;
};

export const verifyExternalEmail = async (token: string): Promise<unknown> => {
  const res = await axiosInstance.get(`${BackendPaths.VERIFYEMAILEXTERNAL}?token=${token}`);
  return res.data;
};

export const companyDocUpload = async (
  companyDocumentPayload: CompanyDocumentPayload,
): Promise<CompanyDocUploadResponse> => {
  const formData = new FormData();
  formData.append('coi', companyDocumentPayload.coi, companyDocumentPayload.coi.name);
  formData.append('byLaws', companyDocumentPayload.byLaws, companyDocumentPayload.byLaws.name);
  formData.append(
    'boardResolution',
    companyDocumentPayload.boardResolution,
    companyDocumentPayload.boardResolution.name,
  );
  try {
    const res = await axiosInstance.post(BackendPaths.COMPANYDOCUMENTUPLOAD, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const createOrgWithEin = async (payload: CreateWithEinPayload) => {
  try {
    const res = await axiosInstance.post(BackendPaths.CREATE_ORG_WITH_EIN, payload);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getLinkedBankAccounts = async (): Promise<ILinkedBankAccounts[]> => {
  const res = await axiosInstance.get(BackendPaths.LINKED_BANK_ACCOUNTS);
  return res.data;
};

export const setPrimaryBankAccount = async (accountId: string) => {
  const url = BackendPaths.SET_PRIMARY_BANK_ACCOUNT.replace(':bank_account_id', accountId);
  try {
    const res = await axiosInstance.put(url);
    return res.data;
  } catch (error) {
    throw error;
  }
};
